import React, { useState, useMemo } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Order } from '@/context/OrdersContext';
import { formatCurrency } from '@/utils/formatCurrency';
import { netProfit } from '@trader-dashboard/shared';
import { MoreVertical, Upload } from 'lucide-react';

type OrdersTableProps = {
  orders: Order[];
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onImportClick?: () => void;
};

const getOrderTypeName = (type: number): string => {
  const types: { [key: number]: string } = {
    0: 'Buy',
    1: 'Sell',
    2: 'Buy Limit',
    3: 'Sell Limit',
    4: 'Buy Stop',
    5: 'Sell Stop',
  };
  return types[type] || `Type ${type}`;
};

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  return status === 'OPEN' ? 'default' : 'secondary';
};

export const OrdersTable = ({
  orders,
  loading = false,
  hasMore = false,
  onLoadMore,
  onImportClick,
}: OrdersTableProps): React.ReactNode => {
  const [filterSymbol, setFilterSymbol] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      if (filterSymbol !== 'all' && order.symbol !== filterSymbol) return false;
      if (filterStatus !== 'all' && order.status !== filterStatus) return false;
      if (filterType !== 'all' && order.type.toString() !== filterType) return false;
      return true;
    });
  }, [orders, filterSymbol, filterStatus, filterType]);

  const uniqueSymbols = useMemo(() => {
    const symbols = [...new Set(orders.map(order => order.symbol))].filter(s => s && s.trim() !== '');
    return symbols.sort();
  }, [orders]);

  const uniqueTypes = useMemo(() => {
    const types = [...new Set(orders.map(order => order.type))];
    return types.sort((a, b) => a - b);
  }, [orders]);

  if (loading && orders.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="text-center">Loading orders...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="grid gap-2">
            <CardTitle className="text-2xl">Trading Orders</CardTitle>
            <CardDescription>
              Complete history of your trading orders with detailed information.
            </CardDescription>
          </div>

          {/* Filters + Actions */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Symbol</label>
              <Select value={filterSymbol} onValueChange={setFilterSymbol}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {uniqueSymbols.map(symbol => (
                    <SelectItem key={symbol} value={symbol}>{symbol}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Type</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {uniqueTypes.map(type => (
                    <SelectItem key={type} value={type.toString()}>
                      {getOrderTypeName(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 3-dot menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">More actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onImportClick}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Orders
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ticket</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Lots</TableHead>
              <TableHead className="text-right">Open Price</TableHead>
              <TableHead>Open Time</TableHead>
              <TableHead className="text-right">Close Price</TableHead>
              <TableHead>Close Time</TableHead>
              <TableHead className="text-right">Gross Profit</TableHead>
              <TableHead className="text-right">Swap</TableHead>
              <TableHead className="text-right">Commission</TableHead>
              <TableHead className="text-right">Net Profit</TableHead>
              <TableHead className="text-right">SL</TableHead>
              <TableHead className="text-right">TP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow key={order._id}>
                <TableCell className="font-mono">{order.ticket}</TableCell>
                <TableCell className="font-semibold">{order.symbol}</TableCell>
                <TableCell>
                  <Badge variant="outline">{getOrderTypeName(order.type)}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusVariant(order.status)}>
                    {order.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono">{order.lots}</TableCell>
                <TableCell className="text-right font-mono">{order.open_price.toFixed(5)}</TableCell>
                <TableCell>
                  {new Date(order.open_time * 1000).toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {order.close_price ? order.close_price.toFixed(5) : '-'}
                </TableCell>
                <TableCell>
                  {order.close_time ? new Date(order.close_time * 1000).toLocaleString() : '-'}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {order.profit !== undefined ? (
                    <span className={order.profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(order.profit)}
                    </span>
                  ) : '-'}
                </TableCell>
                {/* Costs show '-' when never captured (pre-resync orders) and
                    $0.00 when genuinely zero, e.g. no swap on an intraday trade. */}
                <TableCell className="text-right font-mono">
                  {order.swap !== undefined ? formatCurrency(order.swap) : '-'}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {order.commission !== undefined ? formatCurrency(order.commission) : '-'}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {order.profit !== undefined ? (
                    <span className={netProfit(order) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(netProfit(order))}
                    </span>
                  ) : '-'}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {order.sl && order.sl > 0 ? order.sl.toFixed(5) : '-'}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {order.tp && order.tp > 0 ? order.tp.toFixed(5) : '-'}
                </TableCell>
              </TableRow>
            ))}

            {filteredOrders.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={15} className="text-center text-muted-foreground py-8">
                  {orders.length === 0 ? 'No orders found' : 'No orders match the current filters'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {hasMore && (
          <div className="mt-6 text-center">
            <Button
              onClick={onLoadMore}
              disabled={loading}
              variant="outline"
            >
              {loading ? 'Loading...' : 'Load More Orders'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
